// Map block (templates/components/map.html.twig): OpenStreetMap with Leaflet. The tiles are served by the own
// server (src/Controller/Website/MapTileController.php), so no visitor data goes to a third party. Leaflet and its
// stylesheet are a chunk of their own, loaded shortly before a map scrolls into view.
const OSM = '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>';
const STYLES = {
    standard: {maxZoom: 19, attribution: OSM},
    hot: {
        maxZoom: 19,
        attribution: OSM + ', <a href="https://www.hotosm.org/" target="_blank" rel="noopener">HOT</a>',
    },
};
const PIN = '<svg viewBox="0 0 24 30" width="32" height="40" aria-hidden="true"><path d="M12 0C5.4 0 0 5.2 0 11.7 0 20 12 30 12 30s12-10 12-18.3C24 5.2 18.6 0 12 0z" fill="currentColor"/><circle cx="12" cy="11.5" r="4.5" fill="#fff"/></svg>';

const buildPopup = (root) => {
    const popup = document.createElement('div');
    popup.className = 'map-block__popup';

    if (root.dataset.title) {
        const title = document.createElement('strong');
        title.textContent = root.dataset.title;
        popup.appendChild(title);
    }

    JSON.parse(root.dataset.lines || '[]').forEach((line) => {
        const row = document.createElement('div');
        row.textContent = line;
        popup.appendChild(row);
    });

    return popup;
};

// Route planner under the map: "my position" fills the start field with the coordinates of the device (the browser asks
// for permission). The form itself is sent by the visitor, see src/Controller/Website/MapRouteController.php.
const initRoute = (form) => {
    const button = form.querySelector('[data-map-locate]');
    const status = form.querySelector('[data-map-status]');
    if (!button || !navigator.geolocation) {
        return;
    }

    button.hidden = false;
    button.addEventListener('click', () => {
        status.textContent = '';
        navigator.geolocation.getCurrentPosition(
            ({coords}) => {
                form.elements.from.value = coords.latitude.toFixed(6) + ',' + coords.longitude.toFixed(6);
                status.textContent = button.dataset.found;
            },
            () => {
                status.textContent = button.dataset.failed;
            },
            {timeout: 15000}
        );
    });
};

const showMap = async (root) => {
    root.classList.add('is-loading');

    try {
        const {default: L} = await import(/* webpackChunkName: "leaflet" */ './leaflet.js');
        const canvas = root.querySelector('.map-block__canvas');
        canvas.hidden = false; // Leaflet measures its container, so it has to be visible first

        const style = STYLES[root.dataset.style] ? root.dataset.style : 'standard';
        const position = [parseFloat(root.dataset.lat), parseFloat(root.dataset.lng)];
        const map = L.map(canvas, {scrollWheelZoom: false, attributionControl: false}).setView(position, parseInt(root.dataset.zoom, 10) || 16);

        // the attribution of OpenStreetMap is mandatory, the "Leaflet" prefix is left out
        L.control.attribution({prefix: false}).addTo(map);
        L.tileLayer('/map-tiles/' + style + '/{z}/{x}/{y}.png', STYLES[style]).addTo(map);

        const icon = L.divIcon({className: 'map-block__marker', html: PIN, iconSize: [32, 40], iconAnchor: [16, 40], popupAnchor: [0, -36]});
        L.marker(position, {icon}).addTo(map).bindPopup(buildPopup(root), {minWidth: 160});

        root.classList.remove('is-loading');
        root.classList.add('is-active');
        // wheel zoom only after the visitor used the map, so scrolling the page never gets stuck on it
        map.once('focus', () => map.scrollWheelZoom.enable());
    } catch (error) {
        // the address and the OpenStreetMap link stay on the page
        console.error('Map could not be loaded', error);
        root.classList.remove('is-loading');
    }
};

export default function initMap() {
    document.querySelectorAll('[data-map-route]').forEach(initRoute);

    const maps = document.querySelectorAll('[data-map]');
    if (!maps.length) {
        return;
    }

    if (!('IntersectionObserver' in window)) {
        maps.forEach(showMap);
        return;
    }

    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                observer.unobserve(entry.target);
                showMap(entry.target);
            }
        });
    }, {rootMargin: '300px'});
    maps.forEach((root) => observer.observe(root));
}
